import { styled } from '@mui/material/styles';
import { Box, Button, Paper, Typography } from '@mui/material';

export const PanelOverlay = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 1201,
  opacity: 0,
  pointerEvents: 'none',
  // Abrir: más lento y elegante (500ms), Cerrar: más rápido (300ms)
  transition: 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1)',
  willChange: 'opacity',
  '&.open': {
    opacity: 1,
    pointerEvents: 'auto',
    transition: 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1)', // Abrir: elegante y suave
  },
  '&.closed': {
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)', // Cerrar: más rápido
  },
}));

export const PanelContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  right: 0,
  height: '100%',
  width: '80%',
  maxWidth: '70rem',
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  zIndex: 1202,
  // Keep panel fully opaque; only overlay fades (siblings, not nested).
  opacity: 1,
  transform: 'translateX(100%)',
  willChange: 'transform',
  display: 'flex',
  flexDirection: 'column',
  pointerEvents: 'auto',
  // Sincronizado con overlay: 500ms abrir, 300ms cerrar
  transition: 'transform 500ms cubic-bezier(0.4, 0, 0.2, 1)',
  '&.open': {
    transform: 'translateX(0)',
    transition: 'transform 500ms cubic-bezier(0.4, 0, 0.2, 1)', // Sincronizado con overlay
  },
  '&.closed': {
    transform: 'translateX(100%)',
    pointerEvents: 'none',
    transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)', // Sincronizado con overlay
  },
}));

export const PanelHeader = styled(Box)(({ theme }) => ({
  padding: '12px 16px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

export const PanelTitle = styled('h2')(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  margin: 0,
}));

export const CloseButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.secondary,
  minWidth: 'auto',
  padding: '4px',
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover,
  },
  '&:focus': {
    outline: 'none',
  },
}));

export const LoadingContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(75, 85, 99, 0.5)',
  zIndex: 40,
  transition: 'opacity 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  willChange: 'opacity',
  '&.open': {
    opacity: 1,
  },
  '&.closed': {
    opacity: 0,
    pointerEvents: 'none',
  },
}));

export const LoadingPanel = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  right: 0,
  height: '100%',
  width: '75%', // w-3/4
  maxWidth: '48rem', // max-w-3xl
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  zIndex: 50,
  opacity: 1,
  transform: 'translateX(100%)',
  transition: 'transform 600ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  willChange: 'transform',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  '&.open': {
    transform: 'translateX(0)',
  },
  '&.closed': {
    transform: 'translateX(100%)',
  },
}));

export const LoadingContent = styled(Box)(({ theme }) => ({
  textAlign: 'center',
}));

/** In-panel loading while the same animated shell stays mounted. */
export const PanelLoadingBody = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
}));

export const LoadingSpinner = styled(Box)(({ theme }) => ({
  width: '3rem',
  height: '3rem',
  border: '2px solid transparent',
  borderBottomColor: '#10b981',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  marginBottom: '1rem',
  '@keyframes spin': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },
}));

export const LoadingText = styled('p')(({ theme }) => ({
  color: theme.palette.text.secondary,
  margin: 0,
}));

export const StepContent = styled(Box)(({ theme }) => ({
  padding: '24px',
  overflowY: 'auto',
  flex: 1,
}));

// Confirmation Modal Styles (matching ProductDrawer design - exact copy)
export const ConfirmationOverlay = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1300,
}));

export const CancelConfirmationModal = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  padding: theme.spacing(3),
  maxWidth: '400px',
  width: '100%',
  margin: theme.spacing(2),
}));

export const ConfirmationHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  color: '#0CBBDD', // cyanButton color
}));

export const ConfirmationTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  marginLeft: theme.spacing(1),
}));

export const ConfirmationMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(3),
}));

export const ConfirmationActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
}));

export const PrimaryActionButton = styled(Button)<{ gradient?: string }>(({ theme, gradient }) => ({
  background: gradient || 'linear-gradient(to right, #0CBBDD, #10B981)',
  color: 'white',
  fontWeight: 500,
  textTransform: 'none',
  padding: theme.spacing(1, 2),
  borderRadius: '6px',
  '&:hover': {
    background: gradient || 'linear-gradient(to right, #0aa5c4, #059669)',
  },
  '&:disabled': {
    background: '#9ca3af',
  },
}));

export const SecondaryActionButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 500,
  textTransform: 'none',
  padding: theme.spacing(1, 2),
  borderRadius: '6px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.text.secondary,
    boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
}));

// Una línea corta de orientación arriba de todo el paso 1 (antes de proveedor y buscador),
// sin caja ni color de fondo — reemplaza el cuadro azul largo que vivía dentro del paso.
export const ItemsStepHint = styled(Box)(({ theme }) => ({
  fontSize: '13px',
  color: theme.palette.text.secondary,
  marginBottom: '14px',
}));
